import React from "react";
import SEO from "../component/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="max-w-5xl mx-auto p-6 md:p-12">
      <SEO title="Privacy Policy - K Bakery" description="Read our Privacy Policy to understand how we handle your data." />
      <h1 className="text-4xl md:text-5xl font-bold text-pink-700 mb-6 text-center">
        Privacy Policy – K Bakery
      </h1>

      <p className="text-gray-700 mb-4">
        <strong>Effective Date:</strong> 30 Sept 2025 <br />
        <strong>Last Updated:</strong>  30 Sept 2025
      </p>

      <p className="text-gray-700 mb-6">
        K Bakery (“we,” “our,” or “us”) values and respects the privacy of all
        individuals who visit our website, place an order, or otherwise interact
        with our business. This Privacy Policy sets forth how we collect, use,
        disclose, and safeguard your personal information in accordance with
        applicable privacy laws. By accessing our services, you consent to the
        practices described in this policy.
      </p>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">
          1. Information We Collect
        </h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li><strong>Personal Identifiers:</strong> Name, email address, phone number, billing/shipping address.</li>
          <li><strong>Payment Information:</strong> Limited payment details as processed through secure third-party payment processors (we do not store full credit/debit card details).</li>
          <li><strong>Order History & Preferences:</strong> Details of prior and current orders, product selections, and communication records.</li>
          <li><strong>Technical Data:</strong> IP address, browser type, device identifiers, cookies, and usage data when you access our website or digital platforms.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">
          2. How We Use Your Information
        </h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Processing, fulfilling, and managing your orders.</li>
          <li>Communicating order confirmations, updates, or customer service inquiries.</li>
          <li>Personalizing your experience and tailoring product recommendations.</li>
          <li>Conducting internal business operations, analytics, and quality improvements.</li>
          <li>Complying with legal, tax, or regulatory obligations.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">
          3. Sharing and Disclosure of Information
        </h2>
        <p className="text-gray-700 mb-2">
          We do not sell, trade, or rent personal information to third parties. However, your information may be shared under the following limited circumstances:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li><strong>Service Providers:</strong> Trusted vendors such as payment gateways, delivery partners, and IT support providers who process data on our behalf under strict confidentiality agreements.</li>
          <li><strong>Legal Compliance:</strong> Where required by law, regulation, court order, or governmental authority.</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, restructuring, or sale of business assets, personal data may be transferred as part of the transaction.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">4. Data Security</h2>
        <p className="text-gray-700">
          We implement commercially reasonable technical, physical, and administrative safeguards to protect personal data against unauthorized access, alteration, disclosure, or destruction. However, no data transmission or storage system is completely secure, and we cannot guarantee absolute protection.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">5. Data Retention</h2>
        <p className="text-gray-700">
          We retain personal data only for as long as necessary to fulfill the purposes set out in this Privacy Policy, unless a longer retention period is required by law. Once retention is no longer necessary, data will be securely deleted or anonymized.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">6. Cookies and Tracking Technologies</h2>
        <p className="text-gray-700">
          Our website may use cookies, pixels, or similar technologies to improve functionality, analyze trends, and provide tailored content. You may adjust browser settings to refuse cookies, but certain website features may not function as intended.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">7. Your Rights</h2>
        <p className="text-gray-700 mb-2">
          Depending on your jurisdiction, you may have the following rights:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Right to access the personal data we hold about you.</li>
          <li>Right to request correction of inaccurate or incomplete data.</li>
          <li>Right to request deletion of your data, subject to legal limitations.</li>
          <li>Right to object to or restrict certain processing activities.</li>
          <li>Right to withdraw consent at any time, without affecting prior lawful processing.</li>
        </ul>
        <p className="text-gray-700 mt-2">
          Requests to exercise these rights must be submitted via email to:
          <br />
          <strong>
            <a href="mailto:kbakery1002@gmail.com" className="text-pink-600">kbakery1002@gmail.com</a> or{" "}
            {/* <a href="mailto:contact@kbakeryy.com" className="text-pink-600">contact@kbakeryy.com</a> */}
          </strong>
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">8. Third-Party Links</h2>
        <p className="text-gray-700">
          Our website or communications may contain links to external websites. We are not responsible for the privacy practices, policies, or content of such third-party sites. Users are encouraged to review the privacy policies of those websites independently.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">9. Children’s Privacy</h2>
        <p className="text-gray-700">
          Our services are not directed to individuals under the age of 13 (or the age of majority in your jurisdiction). We do not knowingly collect personal data from minors. If such information is inadvertently collected, it will be promptly deleted upon discovery.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">10. Updates to This Policy</h2>
        <p className="text-gray-700">
          We reserve the right to update or amend this Privacy Policy at any time. Material changes will be communicated by posting the revised version on our website with a new effective date. Continued use of our services after such modifications constitutes acceptance of the updated policy.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">11. Contact Information</h2>
        <p className="text-gray-700">
          For any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal data, you may contact us at:
          <br />
          <strong>
            <a href="mailto:team@kbakeryy.com" className="text-pink-600">team@kbakeryy.com</a>
          </strong>
          <br />
          <strong>
            <a href="mailto:contact@kbakeryy.com" className="text-pink-600">contact@kbakeryy.com</a>
          </strong>
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
